<template>
  <v-card :loading="form.processing" flat>
    <v-card-title>
      <slot name="header"></slot>
    </v-card-title>
    <v-card-text>
      <v-form ref="form" class="mt-4" :disabled="isReadonly || form.processing">
        <div class="label mb-2">Subscription Package</div>
        <v-autocomplete
          v-model="form.subscription_package_id"
          :items="subscriptionPackages"
          :rules="[validationRules.required]"
          :error="form.errors && form.errors.subscription_package_id"
          :error-messages="form.errors.subscription_package_id"
          :loading="isSubscriptionPackageListLoading"
          :search-input.sync="searchInputSubscriptionPackage"
          item-text="name"
          item-value="id"
          solo
          flat
          outlined
          dense
          cache-items
          placeholder="Choose subscription package"
          @change="changeSubscriptionPackage"
        />
        <div class="label mb-2">Student</div>
        <v-autocomplete
          v-model="form.student_id"
          :items="students"
          :rules="[validationRules.required]"
          :error="form.errors && form.errors.student_id"
          :error-messages="form.errors.student_id"
          :loading="isStudentListLoading"
          :search-input.sync="searchInputStudent"
          item-text="name"
          item-value="id"
          solo
          flat
          outlined
          dense
          cache-items
          placeholder="Choose student"
        />
        <v-row align="center">
          <v-col md="3" cols="12">
            <div class="label mb-2">Monthly Fee</div>
            <v-text-field
              v-model="monthlyFee"
              :rules="[validationRules.required]"
              :error="form.errors.monthly_fee"
              :error-messages="form.errors.monthly_fee"
              prefix="IDR"
              suffix="per month"
              class="input-right"
              solo
              flat
              outlined
              dense
              placeholder="Montlhly Fee"
            />
          </v-col>
          <v-col md="3" cols="12">
            <div class="label mb-2">Deposit Amount</div>
            <v-text-field
              v-model="depositAmount"
              :error="form.errors.deposit_amount"
              :error-messages="form.errors.deposit_amount"
              prefix="IDR"
              class="input-right"
              solo
              flat
              outlined
              dense
              placeholder="Deposit Amount"
            />
          </v-col>
          <v-col md="6" cols="12">
            <v-checkbox
              v-model="form.is_trial"
              label="1 Month Trial?"
              hint="Jika trial, maka deposit akan dikenakan pada bulan kedua (apabila murid lanjut)"
              persistent-hint
            />
          </v-col>
        </v-row>
        <div class="label mb-2">Status</div>
        <v-select
          v-model="form.status"
          :items="statusOptions"
          :rules="[validationRules.required]"
          :error="form.errors && form.errors.stats"
          :error-messages="form.errors.status"
          solo
          flat
          outlined
          dense
          placeholder="Select subscription status"
        />
        <v-card flat>
          <v-card-title class="px-0">
            <v-row>
              <v-col cols="12" md="auto">Invoices</v-col>
              <v-col cols="12" md="auto">
                <v-btn
                  v-if="isCanAddInvoice"
                  color="warning"
                  depressed
                  @click="addInvoice"
                >
                  <v-icon left>mdi-plus</v-icon>Add Invoice
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="px-0">
            <v-expansion-panels class="my-4">
              <v-expansion-panel
                v-for="(invoice, i) in form.invoices"
                :key="`invoice-${i}`"
              >
                <v-expansion-panel-header>
                  {{ invoice.code ? invoice.code : `Invoice - ${i + 1}` }}
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card :disabled="isReadonly" flat>
                    <v-card-text class="px-0">
                      <v-container fluid>
                        <v-row>
                          <v-col md="3" cols="12">
                            <div class="label mb-2">Term</div>
                            <v-select
                              v-model="invoice.term"
                              :items="terms"
                              :rules="[validationRules.required]"
                              :error="isInvoiceError(i, 'term')"
                              :error-messages="invoiceErrorMessage(i, 'term')"
                              solo
                              flat
                              outlined
                              dense
                            />
                          </v-col>
                          <v-col md="3" cols="12">
                            <div class="label mb-2">Month</div>
                            <v-select
                              v-model="invoice.month"
                              :items="months"
                              :rules="[validationRules.required]"
                              :error="isInvoiceError(i, 'month')"
                              :error-messages="invoiceErrorMessage(i, 'month')"
                              solo
                              flat
                              outlined
                              dense
                            />
                          </v-col>
                          <v-col md="3" cols="12">
                            <div class="label mb-2">Issue Date</div>
                            <v-menu
                              ref="issueDateMenus"
                              v-model="issueDateMenus[i]"
                              :close-on-content-click="false"
                              transition="scale-transition"
                              offset-y
                              min-width="auto"
                            >
                              <template #activator="{ on, attrs }">
                                <v-text-field
                                  v-model="invoice.issue_date"
                                  :rules="[validationRules.required]"
                                  :error="isInvoiceError(i, 'issue_date')"
                                  :error-messages="
                                    invoiceErrorMessage(i, 'issue_date')
                                  "
                                  placeholder="Select issue date"
                                  prepend-icon="mdi-calendar"
                                  solo
                                  flat
                                  outlined
                                  dense
                                  readonly
                                  v-bind="attrs"
                                  v-on="on"
                                ></v-text-field>
                              </template>
                              <v-date-picker
                                v-model="invoice.issue_date"
                                @change="
                                  $refs.issueDateMenus[i].save(
                                    invoice.issue_date
                                  )
                                "
                              ></v-date-picker>
                            </v-menu>
                          </v-col>
                          <v-col md="3" cols="12">
                            <div class="label mb-2">Due Date</div>
                            <v-menu
                              ref="dueDateMenus"
                              v-model="dueDateMenus[i]"
                              :close-on-content-click="false"
                              transition="scale-transition"
                              offset-y
                              min-width="auto"
                            >
                              <template #activator="{ on, attrs }">
                                <v-text-field
                                  v-model="invoice.due_date"
                                  :rules="[validationRules.required]"
                                  :error="isInvoiceError(i, 'due_date')"
                                  :error-messages="
                                    invoiceErrorMessage(i, 'due_date')
                                  "
                                  placeholder="Select due date"
                                  prepend-icon="mdi-calendar"
                                  solo
                                  flat
                                  outlined
                                  dense
                                  readonly
                                  v-bind="attrs"
                                  v-on="on"
                                ></v-text-field>
                              </template>
                              <v-date-picker
                                v-model="invoice.due_date"
                                @change="
                                  $refs.dueDateMenus[i].save(invoice.due_date)
                                "
                              ></v-date-picker>
                            </v-menu>
                          </v-col>
                        </v-row>
                        <div class="label mb-2">Notes</div>
                        <v-textarea
                          v-model="invoice.notes"
                          solo
                          flat
                          outlined
                          dense
                          auto-grow
                          placeholder="Additional notes on invoice"
                        >
                        </v-textarea>
                        <!-- Invoice Item -->
                        <v-card flat>
                          <v-card-title class="px-0">
                            <v-row>
                              <v-col cols="12" md="auto">Invoice Items</v-col>
                              <v-col cols="12" md="auto">
                                <v-btn
                                  depressed
                                  color="warning"
                                  :block="$vuetify.breakpoint.mobile"
                                  @click="addInvoiceItem(i)"
                                >
                                  <v-icon left>mdi-plus</v-icon>Add Item
                                </v-btn>
                              </v-col>
                            </v-row>
                          </v-card-title>
                          <v-divider></v-divider>
                          <v-card-text class="px-0">
                            <v-list>
                              <template v-for="(item, j) in invoice.items">
                                <v-list-item
                                  :key="`invoice-${i}_item-${j}`"
                                  :disabled="isReadonly"
                                >
                                  <v-list-item-content>
                                    <v-row>
                                      <v-col cols="12" md="3">
                                        <div class="label mb-2">Name</div>
                                        <v-text-field
                                          v-model="item.name"
                                          :rules="[validationRules.required]"
                                          :error="
                                            isInvoiceItemError(i, j, 'name')
                                          "
                                          :error-messages="
                                            invoiceItemErrorMessage(
                                              i,
                                              j,
                                              'name'
                                            )
                                          "
                                          solo
                                          flat
                                          outlined
                                          dense
                                          placeholder="Item name"
                                        />
                                      </v-col>
                                      <v-col cols="12" md="2">
                                        <div class="label mb-2">Notes</div>
                                        <v-text-field
                                          v-model="item.notes"
                                          solo
                                          flat
                                          outlined
                                          dense
                                          placeholder="Notes"
                                        />
                                      </v-col>
                                      <v-col cols="12" md="1">
                                        <div class="label mb-2">Qty</div>
                                        <v-text-field
                                          v-model="item.quantity"
                                          :rules="[validationRules.required]"
                                          :error="
                                            isInvoiceItemError(i, j, 'quantity')
                                          "
                                          :error-messages="
                                            invoiceItemErrorMessage(
                                              i,
                                              j,
                                              'quantity'
                                            )
                                          "
                                          type="number"
                                          solo
                                          flat
                                          outlined
                                          dense
                                          placeholder="Quantity"
                                          @change="
                                            calculateInvoiceItem(invoice, j)
                                          "
                                        />
                                      </v-col>
                                      <v-col cols="12" md="2">
                                        <div class="label mb-2">Unit Price</div>
                                        <v-text-field
                                          v-model="item.price"
                                          :rules="[validationRules.required]"
                                          :error="
                                            isInvoiceItemError(i, j, 'price')
                                          "
                                          :error-messages="
                                            invoiceItemErrorMessage(
                                              i,
                                              j,
                                              'price'
                                            )
                                          "
                                          prefix="IDR"
                                          class="input-right"
                                          solo
                                          flat
                                          outlined
                                          dense
                                          placeholder="Price per unit"
                                          @change="
                                            calculateInvoiceItem(invoice, j)
                                          "
                                        />
                                      </v-col>
                                      <v-col cols="12" md="4">
                                        <v-row>
                                          <v-col cols="12" md="8">
                                            <div class="label mb-2">
                                              Total Price
                                            </div>
                                            <v-text-field
                                              :value="
                                                formatNumber(item.total_price)
                                              "
                                              :rules="[
                                                validationRules.required,
                                              ]"
                                              prefix="IDR"
                                              class="input-right"
                                              solo
                                              flat
                                              outlined
                                              dense
                                              disabled
                                            />
                                          </v-col>
                                          <v-col
                                            cols="12"
                                            md="4"
                                            align-self="center"
                                          >
                                            <v-btn
                                              depressed
                                              color="error"
                                              @click="removeInvoiceItem(i, j)"
                                            >
                                              <v-icon left
                                                >mdi-delete-forever</v-icon
                                              >
                                              Delete
                                            </v-btn>
                                          </v-col>
                                        </v-row>
                                      </v-col>
                                    </v-row>
                                  </v-list-item-content>
                                </v-list-item>
                                <v-divider
                                  v-if="i < invoice.items.length - 1"
                                  :key="j"
                                ></v-divider>
                              </template>
                            </v-list>
                          </v-card-text>
                        </v-card>
                        <v-row>
                          <v-col cols="12">
                            <div class="label mb-2">Subscription Amount</div>
                            <v-text-field
                              :value="invoiceSubscriptionAmount(i)"
                              :error="isInvoiceError(i, 'subscription_amount')"
                              :error-messages="
                                invoiceErrorMessage(i, 'subscription_amount')
                              "
                              prefix="IDR"
                              class="input-right"
                              solo
                              flat
                              outlined
                              dense
                              placeholder="Monthly Billing Amount"
                              @input="setInvoiceSubscriptionAmount(i, $event)"
                            />
                          </v-col>
                          <v-col cols="12">
                            <div class="label mb-2">Total Item Price</div>
                            <v-text-field
                              :value="invoiceTotalItemPrice(i)"
                              prefix="IDR"
                              class="input-right"
                              solo
                              flat
                              outlined
                              dense
                              disabled
                            />
                          </v-col>
                          <v-col cols="12">
                            <div class="label mb-2">Deposit Amount</div>
                            <v-text-field
                              :value="invoiceDepositAmount(i)"
                              :error="isInvoiceError(i, 'deposit_amount')"
                              :error-messages="
                                invoiceErrorMessage(i, 'deposit_amount')
                              "
                              prefix="IDR"
                              class="input-right"
                              solo
                              flat
                              outlined
                              dense
                              placeholder="Deposit Amount"
                              @input="setInvoiceDepositAmount(i, $event)"
                            />
                          </v-col>
                          <v-col cols="12">
                            <div class="label mb-2">Total Billed Amount</div>
                            <v-text-field
                              :value="invoiceTotalBilledAmount(i)"
                              prefix="IDR"
                              class="input-right"
                              solo
                              flat
                              outlined
                              dense
                              disabled
                            />
                          </v-col>
                        </v-row>
                        <div class="label mb-2">Status</div>
                        <v-select
                          v-model="invoice.status"
                          :items="invoiceStatusOptions"
                          :rules="[validationRules.required]"
                          :error="isInvoiceError(i, 'status')"
                          :error-messages="invoiceErrorMessage(i, 'status')"
                          solo
                          flat
                          outlined
                          dense
                          placeholder="Select invoice status"
                        />
                      </v-container>
                    </v-card-text>
                    <v-card-actions
                      v-if="!isReadonly && form.invoices.length > 1"
                      class="justify-end"
                    >
                      <v-btn depressed color="error" @click="removeInvoice(i)">
                        <v-icon left>mdi-delete-forever</v-icon> Delete Invoice
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </v-card>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-container fluid>
        <v-row justify="end">
          <v-col md="3" cols="12">
            <v-btn block depressed outlined @click="cancel">Cancel</v-btn>
          </v-col>
          <v-col md="3" cols="12">
            <v-btn
              block
              depressed
              color="primary"
              :disabled="isReadonly || form.processing"
              @click="submit"
            >
              Save Changes
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card-actions>
  </v-card>
</template>
<script>
import constant from '@/constant'
import { mapActions, mapGetters } from 'vuex'
export default {
  name: 'SubscriptionOrderForm',
  props: {
    mode: {
      type: String,
      required: true,
      validator: () => ['add', 'edit', 'view'],
    },
    subscriptionOrder: {
      type: Object,
      default: null,
      required: false,
    },
    subscriptionPackageId: {
      type: Number,
      default: null,
      required: false,
    },
    studentId: {
      type: Number,
      default: null,
      required: false,
    },
  },
  data() {
    return {
      issueDateMenus: [],
      dueDateMenus: [],
      form: this.$inertia.form({
        subscription_package_id: null,
        student_id: null,
        is_trial: false,
        monthly_fee: 0,
        deposit_amount: 0,
        status: null,
        invoices: [],
      }),
      searchInputStudent: null,
      searchInputSubscriptionPackage: null,
    }
  },
  computed: {
    ...mapGetters({
      studentList: 'student/searchList',
      isStudentListLoading: 'student/isSearchListLoading',
      subscriptionPackageList: 'subscriptionPackage/searchList',
      isSubscriptionPackageListLoading:
        'subscriptionPackage/isSearchListLoading',
    }),
    isReadonly() {
      return this.mode === 'view'
    },
    statusOptions() {
      return constant.subscriptionOrderStatusOptions
    },
    students() {
      return this.mode === 'add'
        ? this.studentList.data.filter(
            (e) =>
              e.active_subscriptions.length === 0 ||
              e.active_subscriptions.findIndex(
                (item) =>
                  item.subscription_package_id ===
                  this.form.subscription_package_id
              ) === -1
          )
        : this.studentList.data
    },
    subscriptionPackages() {
      return this.subscriptionPackageList.data
    },
    monthlyFee: {
      get() {
        return this.formatNumber(this.form.monthly_fee)
      },
      set(value) {
        this.form.monthly_fee = this.parseNumber(value)
      },
    },
    depositAmount: {
      get() {
        return this.formatNumber(this.form.deposit_amount)
      },
      set(value) {
        this.form.deposit_amount = this.parseNumber(value)
      },
    },
    isCanAddInvoice() {
      return (
        !this.isReadonly &&
        (this.form.status != 'CANCEL' || this.form.status != 'DONE')
      )
    },
    invoiceStatusOptions() {
      return constant.invoiceStatusOptions
    },
  },
  watch: {
    $props: {
      immediate: true,
      handler() {
        this.validateProps()
      },
    },
    searchInputStudent(val) {
      if (val) {
        this.debouncedSearchStudentList()
      }
    },
    searchInputSubscriptionPackage(val) {
      if (val) {
        this.debouncedSearchSubscriptionPackageList()
      }
    },
  },
  created() {
    this.init()
    this.debouncedSearchStudentList = _.debounce(this.searchStudentList, 500)
    this.debouncedSearchSubscriptionPackageList = _.debounce(
      this.searchSubscriptionPackageList,
      500
    )
  },
  methods: {
    ...mapActions({
      getStudentList: 'student/search',
      getSubscriptionPackageList: 'subscriptionPackage/search',
    }),
    validateProps() {
      if (
        (this.mode === 'edit' || this.mode === 'view') &&
        !this.subscriptionOrder
      ) {
        console.error(
          `subscription order props must be provided for 'edit' and 'view' mode`
        )
      }
    },
    async init() {
      if (this.mode != 'add' && this.subscriptionOrder != null) {
        _.merge(this.form, this.subscriptionOrder)
        await Promise.all([
          this.getStudentList({
            filters: {
              id: this.subscriptionOrder.student_id,
            },
          }),
          this.getSubscriptionPackageList({
            filters: {
              id: this.subscriptionOrder.subscription_package_id,
            },
          }),
        ])
      } else {
        if (this.studentId) {
          await this.getStudentList({
            filters: {
              id: this.studentId,
            },
          })
          this.form.student_id = this.studentId
        }
        if (this.subscriptionPackageId) {
          await this.getSubscriptionPackageList({
            filters: {
              id: this.subscriptionOrder.subscription_package_id,
            },
          })
          this.form.subscription_package_id = this.subscriptionPackageId
          this.changeSubscriptionPackage(this.subscriptionPackageId)
        }
      }
    },
    async searchStudentList() {
      await this.getStudentList({
        search: this.searchInputStudent,
        filters: {
          status: 'ACTIVE',
        },
      })
    },
    async searchSubscriptionPackageList() {
      await this.getSubscriptionPackageList({
        search: this.searchInputSubscriptionPackage,
        filters: {
          status: 'ACTIVE',
        },
      })
    },
    changeSubscriptionPackage(value) {
      const subscriptionPackage = this.subscriptionPackages.find(
        (e) => e.id == value
      )
      if (subscriptionPackage) {
        this.form.monthly_fee = subscriptionPackage.monthly_fee
        this.form.deposit_amount = subscriptionPackage.deposit_amount
      }
    },

    addInvoice() {
      this.form.invoices.push({
        status: 'PENDING',
        issue_date: null,
        due_date: null,
        notes: null,
        subscription_amount: parseFloat(this.form.monthly_fee),
        total_price: 0,
        deposit_amount: 0,
        total_billed_amount: 0,
        term: null,
        month: null,
        items: [],
      })
    },
    removeInvoice(index) {
      this.form.invoices.splice(index, 1)
    },
    isInvoiceError(index, prop) {
      const errorProp = `invoices.${index}.${prop}`
      return this.form.errors.invoices && this.form.errors[errorProp]
    },
    invoiceErrorMessage(index, prop) {
      const errorProp = `invoices.${index}.${prop}`
      return this.form.errors.invoices ? this.form.errors[errorProp] : null
    },
    invoiceSubscriptionAmount(index) {
      return this.formatNumber(this.form.invoices[index].subscription_amount)
    },
    setInvoiceSubscriptionAmount(index, value) {
      this.form.invoices[index].subscription_amount = this.parseNumber(value)
    },
    invoiceDepositAmount(index) {
      return this.formatNumber(this.form.invoices[index].deposit_amount)
    },
    setInvoiceDepositAmount(index, value) {
      this.form.invoices[index].deposit_amount = this.parseNumber(value)
    },
    invoiceTotalItemPrice(index) {
      const invoice = this.form.invoices[index]
      if (invoice.items.length === 0) {
        return 0
      }
      return invoice.items
        .map((e) => e.total_price)
        .reduce((prev, curr) => prev + curr, 0)
    },
    invoiceTotalBilledAmount(index) {
      const invoice = this.form.invoices[index]
      const totalItemPrice = this.invoiceTotalItemPrice(index)
      const totalBilledAmount =
        totalItemPrice + invoice.subscription_amount - invoice.deposit_amount
      return this.formatNumber(totalBilledAmount)
    },
    addInvoiceItem(index) {
      this.form.invoices[index].items.push({
        name: null,
        notes: null,
        quantity: 0,
        price: 0,
        total_price: 0,
      })
    },
    removeInvoiceItem(index, itemIndex) {
      this.form.invoices[index].items.splice(itemIndex, 1)
    },
    isInvoiceItemError(i, j, prop) {
      const errorProp = `invoices.${i}.items.${j}.${prop}`
      return this.form.errors.invoices && this.form.errors[errorProp]
    },
    invoiceItemErrorMessage(i, j, prop) {
      const errorProp = `invoices.${i}.items.${j}.${prop}`
      return this.form.errors.invoices ? this.form.errors[errorProp] : null
    },
    calculateInvoiceItem(invoice, j) {
      invoice.items[j].total_price =
        invoice.items[j].price * invoice.items[j].quantity
    },
    cancel() {
      this.$emit('cancel')
    },
    async submit() {
      if (this.$refs.form.validate()) {
        if (this.mode === 'add') {
          this.form.post('/subscription-orders')
        } else if (this.mode === 'edit') {
          this.form.put(`/subscription-orders/${this.subscriptionOrder.id}`)
        }
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.input-right {
  :deep(input) {
    text-align: right;
  }
}
</style>
